// Admin CRUD: loading_methods (list, add, edit, delete) — configuration for logistic/shipping methods

import { useState } from 'react';
import { useLoadingMethods } from '@/hooks/use-store';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { LoadingMethod } from '@/lib/store-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';

export default function AdminLoadingMethods() {
  const queryClient = useQueryClient();
  const { data: loadingMethods, isLoading } = useLoadingMethods();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<LoadingMethod | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const { toast } = useToast();

  // Save (create or update) loading method
  const saveMutation = useMutation({
    mutationFn: async () => {
      const row = { name: name.trim(), description: description.trim() || null };
      if (editing) {
        const { error } = await supabase.from('loading_methods').update(row).eq('id', editing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('loading_methods').insert(row);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store', 'lookups', 'loading_methods'] });
      setOpen(false);
      setEditing(null);
      resetForm();
      toast({ title: editing ? 'Loading method updated' : 'Loading method created' });
    },
    onError: (e) => toast({ title: 'Error', description: e instanceof Error ? e.message : 'Failed', variant: 'destructive' }),
  });

  // Delete loading method
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('loading_methods').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store', 'lookups', 'loading_methods'] });
      toast({ title: 'Loading method deleted' });
    },
    onError: (e) => toast({ title: 'Error', description: e instanceof Error ? e.message : 'Failed', variant: 'destructive' }),
  });

  function resetForm() {
    setName('');
    setDescription('');
    setEditing(null);
  }

  function openEdit(method: LoadingMethod) {
    setEditing(method);
    setName(method.name);
    setDescription(method.description ?? '');
    setOpen(true);
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Loading Methods</h1>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
        <DialogTrigger asChild>
          <Button onClick={() => { resetForm(); setEditing(null); }}>Add loading method</Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? 'Edit loading method' : 'New loading method'}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Boxes, Pallets, Container" />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short description" />
            </div>
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !name.trim()}>Save</Button>
          </div>
        </DialogContent>
      </Dialog>
      {isLoading ? (
        <p className="text-secondary">Loading...</p>
      ) : (
        <Table className="mt-6">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(loadingMethods ?? []).map((m) => (
              <TableRow key={m.id}>
                <TableCell>{m.name}</TableCell>
                <TableCell className="text-muted-foreground">{m.description ?? '—'}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(m)}>Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteMutation.mutate(m.id)}>Delete</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
